import { useRef, useState, useCallback } from 'react';

const LONG_PRESS_DURATION = 500; // ms

/**
 * Returns open state + event handlers for a Radix Tooltip that:
 * - Shows on mouse hover only (desktop) — uses onMouseEnter/Leave so touch taps
 *   never accidentally open the tooltip on click.
 * - Shows after a long-press touch (mobile, ≥500 ms hold)
 * - Hides on touch end / move
 */
export function useLongPressTooltip() {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track last touch time to suppress ghost mouseenter fired by mobile browsers
  // after a tap (browsers synthesise mouse events ~300-500 ms after touch end).
  const lastTouchTime = useRef(0);

  const clearTimer = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  // Mouse-only: guard against synthetic mouse events fired right after touch
  const onMouseEnter = useCallback(() => {
    if (Date.now() - lastTouchTime.current < 800) return;
    setOpen(true);
  }, []);
  const onMouseLeave = useCallback(() => setOpen(false), []);

  const onTouchStart = useCallback(() => {
    lastTouchTime.current = Date.now();
    clearTimer();
    timer.current = setTimeout(() => setOpen(true), LONG_PRESS_DURATION);
  }, [clearTimer]);

  const onTouchEnd = useCallback(() => {
    clearTimer();
    setOpen(false);
  }, [clearTimer]);

  const onTouchMove = useCallback(() => {
    clearTimer();
    setOpen(false);
  }, [clearTimer]);

  return {
    open,
    setOpen,
    triggerProps: {
      onMouseEnter,
      onMouseLeave,
      onTouchStart,
      onTouchEnd,
      onTouchMove,
    },
  };
}
